import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment } from "react";
import CloseIcon from "../../../../assets/icons/close-icon";
import { useFormikContext } from "formik";

// One group of choices shown as small selectable cards (same look as the Map
// columns step): a radio dot, the option name and a one-line hint.
const OptionGroup = ({ title, options, value, onSelect }) => (
  <div>
    <div className="text-xs fw_600 uppercase tracking-wide text-[var(--description-color)] mb-2">
      {title}
    </div>
    <div className="grid grid-cols-2 gap-3">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => !selected && onSelect(opt.value)}
            className={`flex items-start gap-2.5 p-3 rounded-lg border text-left transition-colors ${
              selected
                ? "border-[var(--primary)] bg-[var(--row-selection-color)]"
                : "border-[var(--light-border-color)] hover:bg-[var(--row-hover-color)]"
            }`}
          >
            <span
              className={`mt-0.5 shrink-0 w-4 h-4 rounded-full border flex items-center justify-center ${
                selected
                  ? "border-[var(--primary)]"
                  : "border-[var(--button-border-color)]"
              }`}
            >
              {selected && (
                <span className="w-2 h-2 rounded-full bg-[var(--primary)]" />
              )}
            </span>
            <span className="min-w-0">
              <span
                className={`block text-sm fw_600 ${
                  selected
                    ? "text-[var(--primary)]"
                    : "text-[var(--heading-color)]"
                }`}
              >
                {opt.label}
              </span>
              <span className="block text-xs mt-0.5 text-[var(--description-color)]">
                {opt.hint}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const TemplateAddOptModal = ({ isAddOptOpen = false, setIsAddOptOpen }) => {
  const { values, setFieldValue } = useFormikContext();

  // Swaps the GST system fields for the chosen format.
  const onGstFormatChange = (newGstFormat) => {
    setFieldValue("gst_format", newGstFormat);

    let updatedGrnColumns = values.grnColumns.filter(
      (col) =>
        !["item_gst", "item_cgst_percentage", "item_sgst_percentage"].includes(
          col.name
        )
    );

    if (newGstFormat === "gst") {
      updatedGrnColumns = [...updatedGrnColumns, ...gst];
    } else if (newGstFormat === "cgst_sgst") {
      updatedGrnColumns = [...updatedGrnColumns, ...cgst_sgst];
    }

    setFieldValue("grnColumns", updatedGrnColumns);
  };

  // Swaps the expiry system fields for the chosen format.
  const onExpiryFormatChange = (newExpiryFormat) => {
    setFieldValue("exp_format", newExpiryFormat);

    let updatedGrnColumns = values.grnColumns.filter(
      (col) =>
        !["item_exp_date", "item_exp_month", "item_exp_year"].includes(
          col.name
        )
    );

    if (newExpiryFormat === "single") {
      updatedGrnColumns = [...updatedGrnColumns, ...single];
    } else if (newExpiryFormat === "multiple") {
      updatedGrnColumns = [...updatedGrnColumns, ...mutiple];
    }

    setFieldValue("grnColumns", updatedGrnColumns);
  };

  return (
    <Transition show={isAddOptOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => {
          setIsAddOptOpen(false);
        }}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-0"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          {/* This is your AddOpt container */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-0"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto w-[32rem] max-w-full p-5 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base fw_600 text-[var(--heading-color)]">
                    Additional options
                  </h2>
                  <p className="text-xs mt-0.5 text-[var(--description-color)]">
                    Tell us how the vendor's file lays out GST and expiry.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  className="p-1.5 rounded-lg hover:bg-[var(--row-hover-color)]"
                  onClick={() => {
                    setIsAddOptOpen(false);
                  }}
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="flex flex-col gap-5 mt-5">
                <OptionGroup
                  title="GST format"
                  options={gstFormatOptns}
                  value={values?.gst_format}
                  onSelect={onGstFormatChange}
                />
                <OptionGroup
                  title="Expiry format"
                  options={expiryFormatOptns}
                  value={values?.exp_format}
                  onSelect={onExpiryFormatChange}
                />
              </div>

              {/* Footer */}
              <div className="flex justify-end mt-6 pt-4 border-t border-[var(--light-border-color)]">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddOptOpen(false);
                  }}
                  className="h-9 px-6 rounded-lg text-sm fw_500 text-white bg-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)]"
                >
                  Done
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TemplateAddOptModal;

export const gstFormatOptns = [
  { label: "GST", value: "gst", hint: "One GST % column" },
  {
    label: "CGST / SGST",
    value: "cgst_sgst",
    hint: "Separate CGST % and SGST % columns",
  },
];
export const expiryFormatOptns = [
  { label: "Single column", value: "single", hint: "Full expiry date in one column" },
  {
    label: "Multi column",
    value: "multiple",
    hint: "Month and year in separate columns",
  },
];

const cgst_sgst = [
  {
    label: "Item CGST %",
    name: "item_cgst_percentage",
    value: "",
    required: true,
  },
  {
    label: "Item SGST %",
    name: "item_sgst_percentage",
    value: "",
    required: true,
  },
];

const gst = [
  { label: "Item GST %", name: "item_gst", value: "", required: true },
];

const single = [
  {
    label: "Item Expiry Date",
    name: "item_exp_date",
    value: "",
    required: true,
  },
];

const mutiple = [
  {
    label: "Expiry Month",
    name: "item_exp_month",
    value: "",
    required: true,
  },
  {
    label: "Expiry year",
    name: "item_exp_year",
    value: "",
    required: true,
  },
];
